<template>
    <div class="facility-data-download">
        <!-- 다운로드 설정 컨테이너 -->
        <div class="form-container">
            <div class="download-settings">
                <!-- 기간 및 조회 유형 설정 -->
                <h6 class="section-title">수집 데이터 다운로드</h6>
                <div class="download-setting-content">
                    <div class="period-and-query-section">
                        <div class="download-setting-description">
                            조회 기간을 지정하여 계측기의 수집 데이터 또는 집계 데이터를 다운로드할 수 있습니다.<br />
                            기본값으로 전체 데이터가 선택되나 특정 부하 또는 수집 항목을 선택하여 다운로드할 수
                            있습니다.
                        </div>
                        <div class="settings-row">
                            <div class="query-selection">
                                <label>데이터 유형</label>
                                <select v-model="selectedTimeDsvn" class="form-control">
                                    <option value="">조회 유형을 선택하세요</option>
                                    <option value="raw">원시 데이터</option>
                                    <option v-for="timeDsvn in timeDsvn" :key="timeDsvn.value" :value="timeDsvn.value">
                                        {{ timeDsvn.label }}
                                    </option>
                                </select>
                            </div>
                            <div class="date-range">
                                <div class="date-input">
                                    <label>조회 시작</label>
                                    <DatePicker
                                        v-model="fromDate"
                                        type="date"
                                        value-type="format"
                                        placeholder="시작일을 선택하세요"
                                    />
                                </div>
                                <div class="date-input">
                                    <label>조회 종료</label>
                                    <DatePicker
                                        v-model="toDate"
                                        type="date"
                                        value-type="format"
                                        placeholder="종료일을 선택하세요"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- 다운로드 실행 -->
                    <div class="download-execution">
                        <button
                            @click="toggleLoadSelection"
                            class="btn-outline-secondary load-btn"
                            :class="{ active: showLoadSelection }"
                        >
                            <i class="fa fa-list"></i> 부하 선택 {{ showLoadSelection ? "숨기기" : "보기" }}
                        </button>
                        <button
                            @click="toggleDataTypeSettings"
                            class="btn-outline-secondary prop-btn"
                            :class="{ active: showDataTypeSettings }"
                        >
                            <i class="fa fa-cog"></i> 측정 항목
                            {{ showDataTypeSettings ? "숨기기" : "보기" }}
                        </button>
                        <button @click="downloadData" class="btn-primary download-btn" :disabled="isDownloading">
                            <i v-if="isDownloading" class="fa fa-spinner fa-spin"></i>
                            <i v-else class="fa fa-download"></i>
                            데이터 다운로드
                        </button>
                    </div>
                </div>

                <!-- 옵션 설정 -->
                <div v-if="showLoadSelection || showDataTypeSettings" class="options-section">
                    <!-- 부하 선택 -->
                    <div v-if="showLoadSelection" class="option-group">
                        <div class="option-header">
                            <h6 class="option-title"><i class="fa fa-list"></i> 부하 선택</h6>
                            <div class="select-actions">
                                <button @click="selectAllLoads" class="btn btn-sm btn-outline-primary">
                                    전체 선택
                                </button>
                                <button @click="deselectAllLoads" class="btn btn-sm btn-outline-secondary">
                                    전체 해제
                                </button>
                            </div>
                        </div>
                        <div class="load-list">
                            <div class="load-grid">
                                <div
                                    v-for="facility in subFacilityList"
                                    :key="facility.data.facilityIdx"
                                    class="load-item"
                                >
                                    <label class="checkbox-label">
                                        <input
                                            type="checkbox"
                                            v-model="selectedLoads"
                                            :value="facility.data.facilityIdx"
                                        />
                                        <span class="checkmark"></span>
                                        <div class="facility-info">
                                            <span class="facility-name">{{ facility.data.facilityName }}</span>
                                            <span class="facility-addr">({{ facility.data.facilityAddr }})</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 데이터 유형 설정 -->
                    <div v-if="showDataTypeSettings" class="option-group">
                        <div class="option-header">
                            <h6 class="option-title"><i class="fa fa-cog"></i> 측정 항목</h6>
                            <div class="prop-actions">
                                <button @click="selectAllProps" class="btn btn-sm btn-outline-primary">
                                    전체 선택
                                </button>
                                <button @click="deselectAllProps" class="btn btn-sm btn-outline-secondary">
                                    전체 해제
                                </button>
                            </div>
                        </div>
                        <div class="prop-checkbox-grid">
                            <label class="prop-checkbox-label" v-for="prop in availableProps" :key="prop.value">
                                <input type="checkbox" v-model="selectedPropNames" :value="prop" />
                                <span class="prop-checkmark"></span>
                                <span class="prop-name">{{ prop }}</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { mapState, mapMutations } from "vuex";
import { extractFilenameFromResponse } from "@src/utils/exportExcelTitle.js";

export default {
    components: {
        DatePicker,
    },
    props: {
        facilityIdx: {
            type: Number,
            required: true,
        },
        subFacilityList: {
            type: Array,
            required: false,
            default: () => [],
        },
        getPurpsUseCode: {
            type: String,
            required: false,
            default: "",
        },
    },
    data() {
        return {
            selectedLoads: [],
            fromDate: this.getDefaultFromDate(),
            toDate: this.getDefaultToDate(),
            timeDsvn: [],
            selectedTimeDsvn: "",

            selectedPropNames: [],
            isDownloading: false,

            availableProps: [],

            // 토글 상태
            showLoadSelection: false,
            showDataTypeSettings: false,

            //집계 데이터 입력 필수 데이터 유효성 조건
            isRequiredSumData: {
                facilityIdx: {
                    required: true,
                    message: "부하를 선택하세요",
                },
                selectedTimeDsvn: {
                    required: true,
                    message: "조회 유형을 선택하세요.",
                },
                fromDate: {
                    required: true,
                    message: "시작일을 선택하세요.",
                },
                toDate: {
                    required: true,
                    message: "종료일을 선택하세요.",
                },
                childFacilityIdxs: {
                    required: false,
                    message: "부하를 선택하세요.",
                },
                propNames: {
                    required: false,
                    message: "측정 항목을 선택하세요.",
                },
            },
            //원시 데이터 입력 필수 데이터 유효성 조건
            isRequiredRawData: {
                facilityIdx: {
                    required: true,
                    message: "부하를 선택하세요.",
                },

                fromDate: {
                    required: true,
                    message: "시작일을 선택하세요.",
                },
                toDate: {
                    required: true,
                    message: "종료일을 선택하세요.",
                },
                childFacilityIdxs: {
                    required: false,
                    message: "부하를 선택하세요.",
                },
                propNames: {
                    required: false,
                    message: "측정 항목을 선택하세요.",
                },
            },
        };
    },
    watch: {
        selectedLoads: {
            handler() {
                this.getPropNamesWithFacility();
            },
        },
        availableProps: {
            handler(newProps) {
                if (newProps && newProps.length > 0) {
                    this.selectAllProps();
                }
            },
            immediate: true,
        },
    },
    computed: {
        ...mapState({
            commonCodes: (state) => state.commonCodes,
        }),

        childFacilityIdxs() {
            return this.selectedLoads;
        },
        propNames() {
            return this.selectedPropNames;
        },
    },
    mounted() {
        // 기본값으로 모든 부하 선택
        this.selectAllLoads();

        // 부하 데이터가 존재하면 부하 선택 버튼을 활성화된 상태로 렌더링
        if (this.subFacilityList && this.subFacilityList.length > 0) {
            this.showLoadSelection = true;
        }

        this.getTimeDsvn();
    },
    methods: {
        ...mapMutations(["SET_IS_LOADING"]),
        setIsLoadingState(status) {
            this.SET_IS_LOADING(status);
        },
        getTimeDsvn() {
            if (!this.commonCodes.timeDsvn) return;

            this.timeDsvn = this.commonCodes.timeDsvn
                .filter((code) => code.options.type === "Type1")
                .map((code) => ({
                    value: code.value,
                    label: code.text,
                }));
        },
        async getPropNamesWithFacility() {
            try {
                const response = await backEndApi.facility.getPropNamesWithFacility(
                    this.facilityIdx,
                    this.childFacilityIdxs
                );
                if (response.status === 200) {
                    this.availableProps = response.data;
                } else throw new Error(response.data.detail);
            } catch (error) {
                this.alertWarning(error);
                console.error("getPropNamesWithFacility 오류:", error);
            }
        },
        getDefaultFromDate() {
            const date = new Date();
            date.setDate(date.getDate() - 7); // 7일 전
            return this.formatDate(date);
        },
        getDefaultToDate() {
            return this.formatDate(new Date());
        },
        formatDate(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        },
        selectAllLoads() {
            this.selectedLoads = this.subFacilityList.map((facility) => facility.data.facilityIdx);
        },
        deselectAllLoads() {
            this.selectedLoads = [];
        },
        selectAllProps() {
            this.selectedPropNames = this.availableProps.map((prop) => prop);
        },
        deselectAllProps() {
            this.selectedPropNames = [];
        },

        // 토글 메서드들
        toggleLoadSelection() {
            this.showLoadSelection = !this.showLoadSelection;
            if (this.showLoadSelection) {
                this.showDataTypeSettings = false; // 다른 하나는 숨기기
            }
        },

        toggleDataTypeSettings() {
            this.showDataTypeSettings = !this.showDataTypeSettings;
            if (this.showDataTypeSettings) {
                this.showLoadSelection = false; // 다른 하나는 숨기기
            }
        },

        // 통합된 유효성 검사 메서드
        validateData(requiredConfig) {
            const errors = [];

            // required가 true인 필드만 필터링하여 검사
            Object.entries(requiredConfig).forEach(([key, config]) => {
                if (config.required) {
                    let isValid = true;

                    // 동적으로 키에 해당하는 값 검사
                    if (key in this) {
                        const value = this[key];
                        if (Array.isArray(value)) {
                            isValid = value.length > 0;
                        } else {
                            isValid = !!value;
                        }
                    } else {
                        // 키가 this에 없는 경우 기본적으로 유효하지 않음
                        isValid = false;
                    }

                    if (!isValid) {
                        errors.push(config.message);
                    }
                }
            });

            return {
                isValid: errors.length === 0,
                errors: errors,
            };
        },
        dateValidation() {
            const fromDate = new Date(this.fromDate);
            const toDate = new Date(this.toDate);
            const today = new Date();

            if (fromDate > toDate) {
                this.alertWarning("시작일이 종료일보다 클 수 없습니다.");
                return false;
            }

            if (fromDate > today || toDate > today) {
                this.alertWarning("오늘 이후의 날짜는 선택할 수 없습니다.");
                return false;
            }
            return true;
        },
        async downloadData() {
            // 날짜 유효성 검사
            if (!this.dateValidation()) {
                return;
            }

            // 원시 데이터인지 집계 데이터인지 확인
            const isRawData = this.selectedTimeDsvn === "raw";
            let dataType = isRawData ? "원시데이터" : "집계데이터";

            // 유효성 검사 실행 (원시 데이터와 집계 데이터의 필수 조건이 다름)
            const validation = this.validateData(isRawData ? this.isRequiredRawData : this.isRequiredSumData);
            if (!validation.isValid) {
                this.alertWarning(validation.errors.join("\n"));
                return;
            }

            try {
                this.isDownloading = true;
                this.setIsLoadingState(true);
                let response;

                if (isRawData) {
                    if (!this.getPurpsUseCode) {
                        this.alertWarning("활용 목적이 설정되지 않았습니다.");
                        return;
                    }
                    this.alertNoti("다운로드가 백그라운드에서 실행됩니다.");
                    response = await backEndApi.facility.downloadRawDataByFacility(
                        this.facilityIdx,
                        this.getPurpsUseCode,
                        this.fromDate,
                        this.toDate,
                        this.childFacilityIdxs,
                        this.propNames
                    );
                } else {
                    this.alertNoti("다운로드가 백그라운드에서 실행됩니다.");
                    response = await backEndApi.facility.downloadSumDataByFacility(
                        this.facilityIdx,
                        this.selectedTimeDsvn,
                        this.fromDate,
                        this.toDate,
                        this.childFacilityIdxs,
                        this.propNames
                    );
                }

                this.handleDownloadResponse(response, dataType);
            } catch (error) {
                this.alertWarning(error.detail);
            } finally {
                this.isDownloading = false;
                this.setIsLoadingState(false);
            }
        },
        handleDownloadResponse(response, dataType) {
            if (response && response.data) {
                // UTF-8 설정과 함께 Blob 데이터를 파일로 다운로드
                const blob = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=utf-8",
                });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;

                // 서버에서 제공하는 파일명 추출
                const fileName = extractFilenameFromResponse(
                    response,
                    `${dataType}_${this.fromDate}_${this.toDate}.xlsx`
                );

                link.download = fileName;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                window.URL.revokeObjectURL(url);
            }
        },
    },
};
</script>

<style scoped>
.download-setting-description {
    font-size: 12px;
    color: #6c757d;
    margin-bottom: 10px;
    line-height: 1.5;
}

.btn-outline-secondary .active {
    border: none;
    outline: none;
}

.load-btn,
.prop-btn,
.download-btn {
    width: 100%;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 36px;
    border: 1px solid #dee2e6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.load-btn i,
.prop-btn i,
.download-btn i {
    font-size: 12px;
}
.download-setting-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    gap: 20px;
}
.query-selection-and-download {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.facility-data-download {
    margin: 0 auto;
}

.form-container {
    background: #ffffff;
    border-radius: 8px;
}

.container-header {
    background: #f8f9fa;
    padding: 12px 16px;
    border-bottom: 1px solid #dee2e6;
    border-radius: 8px 8px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.container-header h5 {
    margin: 0;
    color: #495057;
    font-size: 12px;
}

.container-header i {
    margin-right: 8px;
    color: #6c757d;
}

.select-actions {
    display: flex;
    gap: 10px;
}

.load-list {
    padding: 10px;
    max-height: 300px;
    overflow-y: auto;
}

.load-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 9px;
}

.load-item {
    margin-bottom: 0;
}

.checkbox-label {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition: all 0.3s ease;
    border: 2px solid #e9ecef;
    background: #ffffff;
    min-height: 50px;
    position: relative;
    height: 100%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.checkbox-label:hover {
    background-color: #f8f9fa;
    border-color: #007bff;
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.15);
    transform: translateY(-1px);
}

.checkbox-label:has(input:checked) {
    border-color: #28a745;
    background-color: #f8fff9;
    box-shadow: 0 4px 8px rgba(40, 167, 69, 0.15);
}

.checkbox-label input[type="checkbox"] {
    margin-right: 12px;
    transform: scale(1.3);
    margin-top: 2px;
    accent-color: #28a745;
    cursor: pointer;
}

.checkbox-label input[type="checkbox"]:checked {
    animation: checkboxPop 0.2s ease-in-out;
}

.facility-info {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.facility-name {
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 3px;
    font-size: 13px;
}

.facility-addr {
    color: #6c757d;
    font-size: 11px;
}

.download-settings {
    /* padding: 5px; */
    display: flex;
    flex-direction: column;
    /* gap: 10px; */
}

.section-title {
    margin-bottom: 10px;
    padding: 8px 0;

    color: #495057;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.section-title i {
    color: #6c757d;
    font-size: 12px;
}

.period-section,
.query-type-section {
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.options-section {
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.period-and-query-section {
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 2px dashed #dee2e6;
    flex: 1;
    min-width: 0;
}

.settings-row {
    display: flex;
    gap: 20px;
    align-items: flex-end;
}

.date-range {
    display: flex;
    gap: 15px;
    flex: 2;
}

.date-input {
    flex: 1;
}

.date-input label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #495057;
    font-size: 12px;
}

.query-selection {
    flex: 1;
    min-width: 200px;
}

.query-selection label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #495057;
    font-size: 12px;
}

.download-execution {
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    width: 200px;
    flex-shrink: 0;
    gap: 12px;
}

.toggle-buttons {
    display: flex;
    flex-direction: column;
    /* gap: 12px; */
}

.action-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    padding: 15px 0;
    border-top: 1px solid #e9ecef;
    border-bottom: 1px solid #e9ecef;
}

.option-group {
    margin-bottom: 20px;
    padding: 15px;
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
}

.option-group:last-child {
    margin-bottom: 0;
}

.option-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e9ecef;
}

.option-title {
    margin: 0;
    color: #495057;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
}

.option-title i {
    color: #6c757d;
    font-size: 12px;
}

.data-type-settings {
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.settings-row {
    display: flex;
    gap: 15px;
}

.setting-group {
    flex: 1;
    min-width: 0;
}

.setting-group.full-width {
    flex: none;
    width: 100%;
}

.setting-group.full-width {
    background: #ffffff;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 15px;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.08);
}

.setting-group label {
    display: block;
    font-weight: 500;
    color: #495057;
    font-size: 12px;
}

.prop-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.prop-actions {
    display: flex;
    gap: 8px;
}

.prop-checkbox-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;

    border-radius: 10px;
}

.prop-checkbox-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 8px 10px;
    border-radius: 8px;
    transition: all 0.3s ease;
    border: 2px solid #e9ecef;
    background: #ffffff;
    position: relative;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.prop-checkbox-label:hover {
    background-color: #f8f9fa;
    border-color: #007bff;
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.15);
    transform: translateY(-1px);
}

.prop-checkbox-label:has(input:checked) {
    border-color: #28a745;
    background-color: #f8fff9;
    box-shadow: 0 4px 8px rgba(40, 167, 69, 0.15);
}

.prop-checkbox-label input[type="checkbox"] {
    margin-right: 10px;
    transform: scale(1.3);
    accent-color: #28a745;
    cursor: pointer;
}

.prop-checkbox-label input[type="checkbox"]:checked {
    animation: checkboxPop 0.2s ease-in-out;
}

@keyframes checkboxPop {
    0% {
        transform: scale(1.3);
    }
    50% {
        transform: scale(1.5);
    }
    100% {
        transform: scale(1.3);
    }
}

.prop-name {
    font-weight: 500;
    color: #2c3e50;
    font-size: 12px;
}

.btn {
    border-radius: 6px;
    transition: all 0.2s;
}

.btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn:disabled {
    transform: none;
    box-shadow: none;
    opacity: 0.6;
}

.form-control {
    border-radius: 6px;
    border: 1px solid #ced4da;
    padding: 6px 10px;
    transition: border-color 0.2s;
    font-size: 13px;
}

.form-control:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* DatePicker 커스텀 스타일 */
.mx-datepicker {
    width: 100%;
}

.mx-input {
    border: 1px solid #ced4da;
    border-radius: 6px;
    padding: 6px 10px;
    transition: border-color 0.2s;
    background-color: #fff;
    font-size: 13px;
}

.mx-input:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
}

.mx-datepicker-main {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.mx-calendar {
    border: none;
}

.mx-calendar-header {
    background: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

.mx-btn {
    border-radius: 4px;
    transition: all 0.2s;
}

.mx-btn:hover {
    background-color: #007bff;
    border-color: #007bff;
}

.mx-table-date .mx-date-item {
    border-radius: 4px;
    transition: all 0.2s;
}

.mx-table-date .mx-date-item:hover {
    background-color: #e3f2fd;
}

.mx-table-date .mx-date-item.active {
    background-color: #007bff;
    color: white;
}

.mx-table-date .mx-date-item.disabled {
    color: #6c757d;
    background-color: #f8f9fa;
    cursor: not-allowed;
}
</style>
